<template>
  <div class="transp-page">
    <!-- Nav bar estilo iOS con botón Atrás -->
    <div class="transp-header">
      <Button icon="pi pi-arrow-left" text rounded severity="secondary" @click="volver" />
      <div class="transp-header__titles">
        <div class="transp-header__title">Transporte</div>
        <div class="transp-header__sub">Territorio {{ terr.zona }}{{ terr.numero }}</div>
      </div>
      <div class="flex gap-2">
        <Button
          label="Buscar"
          icon="pi pi-directions"
          severity="secondary"
          :outlined="tab !== 'buscador'"
          size="small"
          @click="tab = tab === 'buscador' ? 'lineas' : 'buscador'"
        />
        <Button
          label="Guardar"
          icon="pi pi-save"
          size="small"
          :loading="guardando"
          @click="guardar"
        />
      </div>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Panel izquierdo -->
      <div
        class="flex flex-column border-right-1 surface-border overflow-hidden"
        style="width:280px;flex-shrink:0"
      >
        <!-- Panel: líneas -->
        <template v-if="tab === 'lineas'">
          <TransporteLineaPanel
            :lineas="lineas"
            :linea-activa-id="lineaActiva"
            :linea-activa-data="lineaActivaData"
            :sentido-activo="sentidoActivo"
            :modo-edicion="modoEdicion"
            @agregar="({ numero, color }) => agregarLinea(numero, color)"
            @seleccionar="seleccionarLinea"
            @eliminar="eliminarLinea"
            @set-sentido="setSentido"
            @set-modo="setModo"
            @deshacer="deshacerUltimoPunto(lineaActiva, sentidoActivo)"
            @limpiar="limpiarRecorrido(lineaActiva, sentidoActivo)"
            @actualizar-horario="(sentido, campo, val) => actualizarHorario(lineaActiva, sentido, campo, val)"
            style="flex:1;overflow:hidden"
          />

          <!-- Paradas de la línea activa -->
          <div
            v-if="lineaActivaData"
            class="border-top-1 surface-border overflow-auto"
            style="max-height:220px;flex-shrink:0"
          >
            <TransporteParadasPanel
              :linea="lineaActivaData"
              :sentido="sentidoActivo"
              @eliminar="(idx) => eliminarParada(lineaActiva, sentidoActivo, idx)"
            />
          </div>
        </template>

        <!-- Panel: buscador -->
        <TransporteBuscador
          v-else
          ref="buscadorRef"
          :lineas="lineas"
          style="flex:1;overflow:hidden"
          @set-map-mode="(mode) => (mapClickMode = mode)"
        />
      </div>

      <!-- Mapa -->
      <div class="flex-1 relative">
        <div
          v-if="!lineaActiva && tab === 'lineas'"
          class="absolute top-50 left-50 text-center"
          style="transform:translate(-50%,-50%);z-index:1000;pointer-events:none"
        >
          <div class="surface-card border-round p-3 shadow-2 text-color-secondary text-sm">
            Seleccioná una línea para editarla
          </div>
        </div>

        <TransporteMapa
          :lineas="lineas"
          :linea-activa-id="lineaActiva"
          :sentido-activo="sentidoActivo"
          :modo="modoEdicion"
          :center="terr.center"
          :color="terr.color"
          :terr-i-d="terr.zona + terr.numero"
          :origen-latlng="origenLatlng"
          :destino-latlng="destinoLatlng"
          @map-click="onMapClick"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.transp-page {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
}
.transp-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--bg-primary);
  border-bottom: 1px solid var(--separator);
  flex-shrink: 0;
}
.transp-header__titles { flex: 1; text-align: left; }
.transp-header__title {
  font-size: var(--text-title-3);
  font-weight: var(--weight-semibold);
  color: var(--label-primary);
}
.transp-header__sub {
  font-size: var(--text-footnote);
  color: var(--label-secondary);
}
</style>

<script setup>
import { ref, computed } from 'vue'
import { useTerritorioStore } from '@/store/territorio'
import { useRouterStore } from '@/store/router'
import { useTransporte } from '@/composables/useTransporte'
import TransporteLineaPanel from '@/components/Transporte/TransporteLineaPanel.vue'
import TransporteParadasPanel from '@/components/Transporte/TransporteParadasPanel.vue'
import TransporteBuscador from '@/components/Transporte/TransporteBuscador.vue'
import TransporteMapa from '@/components/Transporte/TransporteMapa.vue'

const terr = useTerritorioStore()
const route = useRouterStore()

const {
  lineas,
  lineaActiva,
  sentidoActivo,
  modoEdicion,
  lineaActivaData,
  guardando,
  agregarLinea,
  eliminarLinea,
  seleccionarLinea,
  agregarParada,
  eliminarParada,
  agregarPuntoRecorrido,
  limpiarRecorrido,
  deshacerUltimoPunto,
  actualizarHorario,
  setSentido,
  setModo,
  guardar,
} = useTransporte()

const tab = ref('lineas') // 'lineas' | 'buscador'
const mapClickMode = ref(null) // null | 'origen' | 'destino'
const buscadorRef = ref(null)

const origenLatlng = computed(() => buscadorRef.value?.origenLatlng ?? null)
const destinoLatlng = computed(() => buscadorRef.value?.destinoLatlng ?? null)

const onMapClick = (latlng) => {
  // Modo buscador: pin de origen o destino
  if (mapClickMode.value === 'origen' || mapClickMode.value === 'destino') {
    buscadorRef.value?.setLatlng(mapClickMode.value, latlng)
    mapClickMode.value = null
    return
  }

  // Sin línea activa: ignorar
  if (!lineaActiva.value) return

  if (modoEdicion.value === 'paradas') {
    agregarParada(lineaActiva.value, sentidoActivo.value, latlng)
  } else {
    agregarPuntoRecorrido(lineaActiva.value, sentidoActivo.value, latlng)
  }
}

const volver = () => route.$patch({ page: 'formTerritorio', params: { id: terr.uuid } })
</script>
